/* ============================================================
   GELADI WISHLIST - localStorage-backed favorites

   No backend, no app, no login. Works for every visitor.
   Single source of truth in localStorage; every UI piece
   (product toggle, header counter, favorites grid) reacts to
   the `geladi:wishlist:change` event, so all of them stay in
   sync - including across browser tabs (via the `storage` event).

   Public API (window.GeladiWishlist):
     .getAll()        -> [item, ...]   (newest first)
     .count()         -> Number
     .has(id)         -> Boolean
     .add(item)       -> void
     .remove(id)      -> void
     .toggle(item)    -> Boolean  (true if now saved)
     .clear()         -> void

   An `item` is a plain object captured from data-* attributes:
     { id, handle, title, url, image, price }
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'geladi:wishlist:v1';
  var CHANGE_EVENT = 'geladi:wishlist:change';

  /* ---------- storage (defensive: private mode / quota) ---------- */
  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function writeStore(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      return false;
    }
  }

  function emit() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { items: readStore() } })
    );
  }

  /* ---------- core API ---------- */
  var Wishlist = {
    key: STORAGE_KEY,
    event: CHANGE_EVENT,

    getAll: function () {
      return readStore();
    },

    count: function () {
      return readStore().length;
    },

    has: function (id) {
      id = String(id);
      return readStore().some(function (it) {
        return String(it.id) === id;
      });
    },

    add: function (item) {
      if (!item || item.id == null || item.id === '') return;
      var items = readStore();
      var id = String(item.id);
      if (
        items.some(function (it) {
          return String(it.id) === id;
        })
      ) {
        return;
      }
      items.unshift({
        id: id,
        handle: item.handle || '',
        title: item.title || '',
        url: item.url || '',
        image: item.image || '',
        price: item.price || '',
        addedAt: Date.now()
      });
      writeStore(items);
      emit();
    },

    remove: function (id) {
      id = String(id);
      var items = readStore().filter(function (it) {
        return String(it.id) !== id;
      });
      writeStore(items);
      emit();
    },

    toggle: function (item) {
      if (!item || item.id == null || item.id === '') return false;
      if (this.has(item.id)) {
        this.remove(item.id);
        return false;
      }
      this.add(item);
      return true;
    },

    clear: function () {
      writeStore([]);
      emit();
    }
  };

  window.GeladiWishlist = Wishlist;

  /* ---------- DOM wiring ---------- */

  // Build an item from a toggle button's data-* attributes.
  function itemFromEl(el) {
    var d = el.dataset;
    return {
      id: d.wishlistId,
      handle: d.wishlistHandle,
      title: d.wishlistTitle,
      url: d.wishlistUrl,
      image: d.wishlistImage,
      price: d.wishlistPrice
    };
  }

  // Reflect saved/unsaved state on a single toggle button.
  function reflectToggle(btn) {
    var saved = Wishlist.has(btn.dataset.wishlistId);
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');

    var labelEl = btn.querySelector('[data-wishlist-label]');
    if (labelEl) {
      var savedLabel = btn.dataset.labelSaved;
      var defaultLabel = btn.dataset.labelDefault || labelEl.textContent;
      if (!btn.dataset.labelDefault) btn.dataset.labelDefault = defaultLabel;
      labelEl.textContent = saved && savedLabel ? savedLabel : btn.dataset.labelDefault;
    }
  }

  function reflectAllToggles() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(reflectToggle);
  }

  // Update every header/badge counter.
  function reflectCounts() {
    var n = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
      if (n === 0) {
        el.setAttribute('hidden', '');
      } else {
        el.removeAttribute('hidden');
      }
    });
  }

  /* ---------- favorites grid (only on /pages/favorite) ---------- */

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function cardHtml(item) {
    var url = escapeHtml(item.url || '#');
    var title = escapeHtml(item.title || '');
    var price = escapeHtml(item.price || '');
    var id = escapeHtml(item.id);
    var media = item.image
      ? '<img class="geladi-fav__media-img" src="' +
        escapeHtml(item.image) +
        '" alt="' +
        title +
        '" loading="lazy" decoding="async">'
      : '<div class="geladi-fav__media-img geladi-fav__media-img--placeholder" aria-hidden="true"></div>';

    return (
      '<article class="geladi-fav__card" data-wishlist-card="' + id + '">' +
        '<button type="button" class="geladi-fav__remove" data-wishlist-remove="' + id + '" aria-label="Elimină din favorite">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<a class="geladi-fav__media" href="' + url + '">' + media + '</a>' +
        '<div class="geladi-fav__body">' +
          '<h3 class="geladi-fav__title"><a href="' + url + '">' + title + '</a></h3>' +
          (price ? '<p class="geladi-fav__price">' + price + '</p>' : '') +
          '<a class="geladi-fav__cta" href="' + url + '">Vezi produsul</a>' +
        '</div>' +
      '</article>'
    );
  }

  function renderGrid() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    var root = grid.closest('[data-wishlist-root]') || document;
    var emptyEl = root.querySelector('[data-wishlist-empty]');
    var contentEl = root.querySelector('[data-wishlist-content]');
    var countEl = root.querySelector('[data-wishlist-page-count]');

    var items = Wishlist.getAll();

    if (countEl) {
      countEl.textContent = items.length === 1 ? '1 produs salvat' : items.length + ' produse salvate';
    }

    if (!items.length) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.removeAttribute('hidden');
      if (contentEl) contentEl.setAttribute('hidden', '');
      return;
    }

    if (emptyEl) emptyEl.setAttribute('hidden', '');
    if (contentEl) contentEl.removeAttribute('hidden');

    // Incremental sync: reuse existing card nodes (so images don't
    // reload), create only new ones, drop only removed ones. Avoids
    // the full-innerHTML rebuild that made removals flicker.
    var nodesById = {};
    grid.querySelectorAll('[data-wishlist-card]').forEach(function (n) {
      nodesById[n.getAttribute('data-wishlist-card')] = n;
    });

    var wanted = {};
    items.forEach(function (item) {
      var id = String(item.id);
      wanted[id] = true;
      var node = nodesById[id];
      if (!node) {
        var tmp = document.createElement('div');
        tmp.innerHTML = cardHtml(item);
        node = tmp.firstElementChild;
        nodesById[id] = node;
      }
      // appendChild on an already-attached node just moves it into
      // the correct order - no re-parse, no image reload.
      grid.appendChild(node);
    });

    Object.keys(nodesById).forEach(function (id) {
      if (!wanted[id]) nodesById[id].remove();
    });
  }

  /* ---------- event delegation ---------- */

  function onClick(e) {
    var toggle = e.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      e.preventDefault();
      Wishlist.toggle(itemFromEl(toggle));
      return;
    }
    var remove = e.target.closest('[data-wishlist-remove]');
    if (remove) {
      e.preventDefault();
      Wishlist.remove(remove.getAttribute('data-wishlist-remove'));
    }
  }

  function refreshAll() {
    reflectAllToggles();
    reflectCounts();
    renderGrid();
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener(CHANGE_EVENT, refreshAll);
    // Cross-tab: another tab changed the store.
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) refreshAll();
    });
    refreshAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
